import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';
import { useStudents } from 'context/StudentsContext';
import { useIncidents } from 'context/IncidentsContext';

const StudentSelector = ({ selectedStudent, onStudentSelect, onCreateNew }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();

  const { students, loading } = useStudents();
  const { incidentsForStudent } = useIncidents();

  // Roster comes straight from the real StudentsContext. Old versions of this
  // component shipped a hardcoded roster (Alex Thompson, Maria Garcia, etc.)
  // that always leaked into live deployments — even PR #41 didn't catch this
  // file. Now every row here corresponds to a real record in your students
  // store.
  const roster = useMemo(() => {
    if (!Array.isArray(students)) return [];
    return students.map((s) => {
      const name = `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.id;
      const studentIncidents = incidentsForStudent
        ? incidentsForStudent(s.id) || []
        : [];
      const lastIncidentTs = studentIncidents
        .map((inc) => inc?.timestamp || inc?.createdAt)
        .filter(Boolean)
        .sort()
        .pop();
      return {
        id: s.id,
        name,
        firstName: s.firstName || '',
        lastName: s.lastName || '',
        grade: s.grade || '',
        avatar: s.photo || null,
        iepStatus: s.status === 'inactive' ? 'Inactive' : 'Active',
        totalIncidents: studentIncidents.length,
        lastIncident: lastIncidentTs || null,
      };
    });
  }, [students, incidentsForStudent]);

  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) return roster;
    const q = searchQuery.toLowerCase();
    return roster.filter((student) => {
      return (
        student.name.toLowerCase().includes(q) ||
        (student.grade || '').toString().toLowerCase().includes(q) ||
        (student.id || '').toLowerCase().includes(q)
      );
    });
  }, [roster, searchQuery]);

  const getIepStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-success/10 text-success';
      case 'Review Pending':
        return 'bg-warning/10 text-warning';
      case 'Inactive':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatLastIncident = (timestamp) => {
    if (!timestamp) return 'No incidents yet';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInMs = now - date;
      if (Number.isNaN(diffInMs) || diffInMs < 0) return '—';
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours < 1) return 'just now';
      if (diffInHours < 24) return `${diffInHours}h ago`;
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    } catch {
      return '—';
    }
  };

  const initialsFor = (name) => {
    if (!name) return '?';
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join('');
  };

  const handleStudentSelect = (student) => {
    onStudentSelect(student);
    setIsExpanded(false);
  };

  const handleCreateNew = () => {
    if (typeof onCreateNew === 'function') onCreateNew();
    navigate('/student-profile-management');
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-card-foreground">
              Student Selection
            </h2>
            <p className="text-sm text-muted-foreground">
              Choose a student to log behavioral incidents
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </div>

      {/* Selected Student Display */}
      {selectedStudent && (
        <div className="p-4 bg-primary/5 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="relative">
              {selectedStudent?.avatar ? (
                <Image
                  src={selectedStudent.avatar}
                  alt={selectedStudent?.name || 'Student'}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
                  {initialsFor(selectedStudent?.name)}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-card-foreground truncate">
                {selectedStudent?.name || selectedStudent?.id}
              </h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {selectedStudent?.grade && <span>{selectedStudent.grade}</span>}
                {selectedStudent?.iepStatus && (
                  <>
                    {selectedStudent?.grade && <span>•</span>}
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${getIepStatusColor(
                        selectedStudent.iepStatus,
                      )}`}
                    >
                      {selectedStudent.iepStatus}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Student List */}
      {(isExpanded || !selectedStudent) && (
        <div className="p-4">
          {/* Search */}
          <div className="mb-4">
            <Input
              type="search"
              placeholder="Search students by name, grade, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full"
            />
          </div>

          {/* Student Grid */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                Loading students…
              </div>
            ) : roster.length === 0 ? (
              <div className="text-center py-10">
                <Icon
                  name="Users"
                  size={40}
                  className="mx-auto mb-3 text-muted-foreground opacity-60"
                />
                <h3 className="text-sm font-medium text-card-foreground mb-1">
                  No students on your roster yet
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add a student profile first, then come back here to log an
                  incident.
                </p>
                <Button variant="default" onClick={handleCreateNew}>
                  <Icon name="Plus" size={16} />
                  Add student profile
                </Button>
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="text-center py-8">
                <Icon
                  name="Search"
                  size={40}
                  className="text-muted-foreground mx-auto mb-3 opacity-50"
                />
                <p className="text-muted-foreground">
                  No students match "{searchQuery}"
                </p>
              </div>
            ) : (
              filteredStudents.map((student) => (
                <div
                  key={student.id}
                  onClick={() => handleStudentSelect(student)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-sm ${
                    selectedStudent?.id === student.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {student.avatar ? (
                        <Image
                          src={student.avatar}
                          alt={student.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                          {initialsFor(student.name)}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-card-foreground truncate">
                          {student.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {student.grade && <span>{student.grade}</span>}
                        {student.grade && <span>•</span>}
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs ${getIepStatusColor(
                            student.iepStatus,
                          )}`}
                        >
                          {student.iepStatus}
                        </span>
                      </div>
                    </div>

                    <div className="text-right">
                      <p className="text-sm font-medium text-card-foreground">
                        {student.totalIncidents}
                      </p>
                      <p className="text-xs text-muted-foreground">incidents</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatLastIncident(student.lastIncident)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Create New Student */}
          {roster.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={handleCreateNew}
                className="w-full"
              >
                <Icon name="Plus" size={16} />
                Add New Student Profile
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentSelector;
